import { Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import Spinner from '/@/components/spinner';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useCurrentCampaignYear } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import FieldMonitoringDashboardPastReports from '/@/views/fields/field-monitoring/dashboard/tabs/past-reports';
import { PastReportsDashboardCards } from '../../growers/tabs/past-campaign';

const GrowerReportsView = () => {
    const growerNodeId = useOwnUserNode();
    const currentCampaignYear = useCurrentCampaignYear();
    const previousCampaignYear = useMemo(() => {
        return currentCampaignYear !== null ? currentCampaignYear - 1 : null;
    }, [currentCampaignYear]);
    const form = useForm<{
        year: number;
    }>({
        defaultValues: {
            year: previousCampaignYear ?? new Date().getFullYear() - 1,
        },
    });

    // Update form value when previousCampaignYear becomes available
    useEffect(() => {
        if (previousCampaignYear !== null) {
            form.setValue('year', previousCampaignYear);
        }
    }, [previousCampaignYear, form]);

    if (!growerNodeId.data?.id) {
        return (
            <Stack height='100%' width='100%' alignItems='center' justifyContent='center'>
                <Spinner />
            </Stack>
        );
    }

    return (
        <PageContainer
            header={
                <Header
                    title={_('Reportes de campaña')}
                    subtitle={_(
                        'Accedé al resumen de resultados de los lotes para analizar el rendimiento y planificar la próxima campaña.'
                    )}
                    actions={[
                        <MuiSelect
                            key='year'
                            {...form.register('year')}
                            onChange={(e) => form.setValue('year', Number(e.target.value))}
                            type='number'
                            value={form.watch('year')}
                            inputLabel={_('Campaña')}
                            options={Array.from({ length: 5 }, (_unused, index) => ({
                                label: `${new Date().getFullYear() - index} / ${new Date().getFullYear() - index + 1}`,
                                value: (new Date().getFullYear() - index).toString(),
                            }))}
                            size='small'
                            data-testid='grower-reports-year-select'
                            sx={{
                                width: {
                                    md: 200,
                                },
                            }}
                        />,
                    ]}
                />
            }
        >
            <Stack direction='column' spacing={3} py={4}>
                <PastReportsDashboardCards
                    year={form.watch('year')}
                    growerNodeId={growerNodeId.data?.id}
                    isRv={false}
                    nodeScope={getNodeScopeFilters('myFields')}
                />
                <FieldMonitoringDashboardPastReports
                    growerNodeId={growerNodeId.data?.id}
                    year={form.watch('year')}
                    nodeScope={getNodeScopeFilters('myFields')}
                />
            </Stack>
        </PageContainer>
    );
};

export default GrowerReportsView;
